/**
 * Settings → General: download my data and close my account (PHP-3327, QA-17).
 *
 * Closing needs fresh proof that it's really the owner: the password (accounts that sign in only with Google
 * or GitHub must have signed in within the last few minutes instead) and, with two-factor authentication on,
 * a current code. The owner is signed out everywhere at once and Identity e-mails a link that restores the
 * account until the grace period ends; the "Keep my account" page lives at /account-closure/cancel.
 */
import { useMutation } from "@tanstack/react-query";
import { Download, UserX } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button, ErrorAlert, Field, Input, Modal, PasswordInput, Select, buttonClass } from "@/components/ui";
import { identity } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { ApiError } from "@/lib/http";
import type { Account, CloseAccountInput, ClosureReason } from "@/lib/types";

import { SettingRow } from "./parts";

export const CLOSURE_REASONS: [ClosureReason, string][] = [
  ["too_expensive", "It costs too much"],
  ["not_using", "I don't use it any more"],
  ["missing_features", "It's missing something I need"],
  ["switching_provider", "I'm moving to another provider"],
  ["privacy", "Privacy concerns"],
  ["other", "Something else"],
];

export const EXPORT_FILENAME = "cortexgrip-account-data.json";

/** An authenticator code is six digits; anything else is taken as a recovery code (e.g. `abcde-12345`). */
export function secondFactor(value: string): Pick<CloseAccountInput, "code" | "recovery_code"> {
  const v = value.trim();
  if (!v) return {};
  return /^\d{6}$/.test(v) ? { code: v } : { recovery_code: v };
}

export function useDownloadMyData() {
  return useMutation({
    mutationFn: async () => {
      const data = await identity.exportMyData();
      downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), EXPORT_FILENAME);
    },
  });
}

/** The Account card row. */
export function DownloadDataRow() {
  const download = useDownloadMyData();
  return (
    <>
      <SettingRow
        icon={<Download className="size-[15px]" strokeWidth={1.75} />}
        title="Your data"
        detail="Download what we hold about you: your account, sign-ins and businesses"
      >
        <button
          type="button"
          className={buttonClass("secondary", "lg")}
          disabled={download.isPending}
          aria-busy={download.isPending}
          onClick={() => download.mutate()}
        >
          {download.isPending ? "Preparing…" : "Download"}
        </button>
      </SettingRow>
      <ErrorAlert error={download.error} />
    </>
  );
}

export function CloseAccountCard({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="flex flex-col gap-3 rounded-[14px] border border-[#ffc4bb] bg-[#fff1ee] p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[#fbd6c9] text-[#d6342f]">
          <UserX className="size-4" strokeWidth={1.75} />
        </span>
        <h2 className="text-base font-semibold text-fg">Close your account</h2>
      </div>
      <p className="text-sm text-[#766e6a]">
        Signs you out everywhere and anonymises your personal data after 30 days. Your businesses stay with
        their other owners.
      </p>
      <div>
        <button type="button" className={buttonClass("danger", "lg")} onClick={() => setOpen(true)}>
          Close account
        </button>
      </div>
      {open && <CloseAccountModal account={account} onClose={() => setOpen(false)} />}
    </section>
  );
}

function CloseAccountModal({ account, onClose }: { account: Account; onClose: () => void }) {
  const navigate = useNavigate();
  const download = useDownloadMyData();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState<ClosureReason | "">("");
  const [understood, setUnderstood] = useState(false);
  const close = useMutation({
    mutationFn: () =>
      identity.closeAccount({
        ...(account.has_password ? { password } : {}),
        ...(account.mfa_enabled ? secondFactor(code) : {}),
        ...(reason ? { reason } : {}),
      }),
    onSuccess: (closure) => {
      // Identity has already revoked every session. The closed page forgets this one once it is showing:
      // signing out while still in the signed-in area would send the guard to the sign-in page instead.
      const until = closure?.scheduled_for;
      void navigate(
        until ? `/account-closure/closed?until=${encodeURIComponent(until)}` : "/account-closure/closed",
        { replace: true, state: { signOut: true } },
      );
    },
  });
  const reasonOf = (err: unknown) => (err instanceof ApiError ? err.reason : null);
  const failed = reasonOf(close.error);
  const passwordError = failed === "INVALID_CREDENTIALS" ? "That password isn't right." : undefined;
  const codeError =
    failed === "MFA_CODE_INVALID"
      ? "That code isn't right. Try the newest code from your app."
      : failed === "MFA_CODE_REQUIRED"
        ? "Enter a code from your authenticator app."
        : undefined;
  const fieldErrors = passwordError || codeError;
  const ready =
    understood && (!account.has_password || password.length > 0) && (!account.mfa_enabled || code.trim());

  return (
    <Modal
      open
      onClose={onClose}
      title="Close your account?"
      footer={
        <>
          <button type="button" className={buttonClass("secondary", "lg")} onClick={onClose}>
            Keep my account
          </button>
          <Button
            variant="danger"
            size="lg"
            loading={close.isPending}
            disabledReason={
              ready ? undefined : "Tick the box and confirm who you are before closing your account."
            }
            onClick={() => close.mutate()}
          >
            {close.isPending ? "Closing…" : "Close account"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-fg-muted">
          <li>You&apos;ll be signed out on every device and won&apos;t be able to sign in.</li>
          <li>
            For 30 days you can change your mind with the link we e-mail to{" "}
            <strong className="text-fg">{account.email}</strong>.
          </li>
          <li>
            After that your personal data is anonymised for good, and you leave every business you belong to.
          </li>
          <li>If you&apos;re the only owner of a business, make another member an owner first.</li>
        </ul>
        <p className="text-sm text-fg-muted">
          Want a copy first?{" "}
          <button
            type="button"
            className="font-medium text-brand-700 underline hover:no-underline dark:text-brand-500"
            disabled={download.isPending}
            aria-busy={download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending ? "Preparing your data…" : "Download your data"}
          </button>
        </p>
        <ErrorAlert error={download.error} />
        {!fieldErrors && <ErrorAlert error={close.error} />}
        {account.has_password ? (
          <Field label="Password" error={passwordError}>
            {(p) => (
              <PasswordInput
                {...p}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
        ) : (
          <p className="text-sm text-fg-muted">
            You sign in with Google or GitHub. If we ask you to sign in again, do that and come back here.
          </p>
        )}
        {account.mfa_enabled && (
          <Field label="Authenticator code" hint="Or one of your recovery codes." error={codeError}>
            {(p) => (
              <Input
                {...p}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
        )}
        <Field label="Why are you leaving? (optional)">
          {(p) => (
            <Select {...p} value={reason} onChange={(e) => setReason(e.target.value as ClosureReason | "")}>
              <option value="">Prefer not to say</option>
              {CLOSURE_REASONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <label className="flex items-start gap-2.5 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-[#d6342f]"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          I understand that my account will be closed and, after 30 days, anonymised.
        </label>
      </div>
    </Modal>
  );
}
